import { fixedUi, fixedUiTpl } from '@/i18n/fixedUi';
import { localizedSiteLabel } from '@/i18n/catalogDisplayText';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Plus } from 'lucide-react';
import TemuOpsPager, { TEMU_OPS_DEFAULT_PAGE_SIZE } from '@/temu-platform/components/TemuOpsPager';
import TemuOpsListToolbar, { downloadCsv } from '@/temu-platform/components/TemuOpsListToolbar';
import TemuSyncProgressSlot from '@/temu-platform/components/TemuSyncProgressSlot';
import TemuSyncMaxPagesModal from '@/temu-platform/components/TemuSyncMaxPagesModal';
import DiscussAutoRuleModal from '@/temu-platform/modules/discuss/DiscussAutoRuleModal';
import { ProductFilterCard, ProductListCard } from '@/temu-platform/modules/products/components/ProductPageCards';
import BatchQueryModal from '@/temu-platform/modules/products/components/BatchQueryModal';
import {
  joinBatchTokens,
  parseBatchTokens,
} from '@/temu-platform/modules/products/components/batchQueryUtils';
import HoverCopyField from '@/temu-platform/modules/products/components/HoverCopyField';
import TemuProductThumb from '@/temu-platform/modules/products/components/TemuProductThumb';
import { useTemuPageShopScope, stackShopDimensionTabs } from '@/temu-platform/hooks/useTemuPageShopScope';
import { useTemuSyncJobs } from '@/temu-platform/context/TemuSyncJobContext';
import { temuPlatformAPI } from '@/temu-platform/api/temuPlatformApi';
import {
  DISCUSS_STATUS_LABELS,
} from '@/temu-platform/constants/temuApiCatalog';
import { temuShopCapabilities } from '@/temu-platform/utils/temuShopCapabilities';
import { togglePageSelection } from '@/temu-platform/utils/temuPageSelection';
import { useGuardedRefresh } from '@/temu-platform/hooks/useGuardedRefresh';
import { formatSiteListSyncedAt } from '@/temu-platform/utils/temuSiteList';
import { fetchAllPages } from '@/temu-platform/utils/temuExportHelpers';
import { buildOpsExportMenu, opsExportLabel } from '@/temu-platform/utils/buildOpsExportMenu';
import {
  discussStatusLabel,
  discussStatusModifier,
  extractDiscussList,
  extractDiscussTotal,
  formatCanBargain,
  formatProductSkuIdList,
  formatReviewSkuPriceLines,
  extractSiteNameList,
  formatSiteNameList,
  latestCostHistory,
  normalizeDiscussImageUrl,
} from '@/temu-platform/modules/discuss/discussDisplay';
import { pickPricingColorLabel } from '@/temu-platform/modules/discuss/rrpDisplay';
import {
  evaluateDiscussAutoAction,
  readDiscussAutoRulesFromPreferences,
} from '@/temu-platform/modules/discuss/discussAutoRules';
import { rememberDiscussPendingForHome } from '@/temu-platform/utils/temuHomeLocalSnapshot';
import { showTemuConfirm } from '@/temu-platform/components/TemuConfirmDialog';
import { showToast } from '@/utils/toast';
import '@/styles/temu-platform/temu-product-ops.css';

const DISCUSS_AUTO_RULE_HINT = '同步核价单后，按已启用规则自动处理「待供应商确认」单据：'
  + '可按「建议价÷申报价」比例，或按建议价绝对金额设置拒绝/同意阈值；中间区间仍需人工处理。';

function toDiscussConfirmInfo(row, auditType) {
  return {
    discussAuditType: String(auditType),
    discussSn: row.discussSn || row.orderSn || row.orderId,
    orderId: Number(row.orderId || row.orderSn || row.discussSn) || undefined,
  };
}

const STATUS_TABS = [
  { value: '', label: '全部' },
  ...Object.entries(DISCUSS_STATUS_LABELS).map(([value, label]) => ({ value, label })),
];

const BARGAIN_REASON_TYPE_OPTIONS = [
  { value: '0', label: '材质' },
  { value: '1', label: '功能' },
  { value: '2', label: '其他' },
  { value: '3', label: '品类' },
  { value: '4', label: '外观' },
  { value: '5', label: '版型' },
  { value: '6', label: '图案' },
  { value: '7', label: '规格尺寸' },
  { value: '8', label: '品牌' },
];

/** Temu 核价金额多为「分」→ 页面编辑用「元」 */
function fenToYuanInput(amount) {
  if (amount === undefined || amount === null || amount === '') return '';
  const n = Number(amount);
  if (!Number.isFinite(n)) return String(amount);
  if (Number.isInteger(n) && Math.abs(n) >= 1) return (n / 100).toFixed(2);
  return String(n);
}

function yuanToFenString(yuan) {
  const n = Number(yuan);
  if (!Number.isFinite(n) || n < 0) return '';
  return String(Math.round(n * 100));
}

function resolveDiscussOrderId(row) {
  const n = Number(row?.orderId || row?.orderSn || row?.discussSn);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

function resolveDiscussSkuIds(row) {
  const list = Array.isArray(row?.productSkuIdList) ? row.productSkuIdList : [];
  if (list.length) return list.map((id) => Number(id)).filter((id) => id > 0);
  const one = Number(row?.productSkuId ?? row?.skuId);
  return one > 0 ? [one] : [];
}

function ProcessDiscussModal({
  open,
  row,
  busy,
  initialMode = 'requote',
  onClose,
  onSubmit,
}) {
  const mode = initialMode || 'requote';
  const [skuPrices, setSkuPrices] = useState([]);
  const [reasonComponents, setReasonComponents] = useState([{ type: '2', reason: '' }]);
  const [externalLinks, setExternalLinks] = useState(['']);

  useEffect(() => {
    if (!open || !row) return;
    setReasonComponents([{ type: '2', reason: '' }]);
    setExternalLinks(['']);
    const currency = row.suggestPriceCurrency || row.priceCurrency || 'CNY';
    const suggest = row.suggestSupplyPrice ?? row.suggestPrice;
    const supply = row.supplyPrice ?? row.declarePrice;
    const skus = Array.isArray(row.skuCostPrices) ? row.skuCostPrices : [];
    if (skus.length) {
      setSkuPrices(skus.map((sku) => {
        const last = latestCostHistory(sku);
        const suggestRaw = sku?.suggestCostPrice ?? suggest;
        const productSkuId = Number(sku?.productSkuId || sku?.skuId || sku?.skuCode) || 0;
        return {
          productSkuId,
          skuCode: sku?.skuCode || (productSkuId ? String(productSkuId) : ''),
          costYuan: fenToYuanInput(suggestRaw != null && suggestRaw !== '' ? suggestRaw : last?.costPrice),
          currency: sku?.suggestCostCurrency || last?.currency || currency,
          lastYuan: fenToYuanInput(last?.costPrice ?? supply),
          lastCurrency: last?.currency || currency,
          suggestYuan: fenToYuanInput(suggestRaw),
          suggestCurrency: sku?.suggestCostCurrency || currency,
        };
      }));
      return;
    }
    const skuIds = resolveDiscussSkuIds(row);
    setSkuPrices(skuIds.map((id) => ({
      productSkuId: id,
      skuCode: String(id),
      costYuan: fenToYuanInput(suggest),
      currency,
      lastYuan: fenToYuanInput(supply),
      lastCurrency: currency,
      suggestYuan: fenToYuanInput(suggest),
      suggestCurrency: currency,
    })));
  }, [open, row, initialMode]);

  if (!open || !row) return null;

  const submit = () => {
    const orderId = resolveDiscussOrderId(row);
    const discussSn = String(row.discussSn || row.orderSn || row.orderId || '');
    if (mode === 'accept' || mode === 'reject') {
      onSubmit({
        confirmInfos: [{
          discussAuditType: mode === 'accept' ? '1' : '2',
          discussSn,
          orderId: orderId || undefined,
        }],
      });
      return;
    }
    if (!(orderId > 0)) {
      showToast(fixedUi("缺少核价单 orderId，无法重新报价"), 'warning');
      return;
    }
    const components = reasonComponents
      .map((c) => ({
        type: Number(c.type),
        reason: String(c.reason || '').trim(),
      }))
      .filter((c) => c.reason);
    if (!components.length) {
      showToast(fixedUi("请填写至少一条议价理由（含原因类型）"), 'warning');
      return;
    }
    if (components.some((c) => !Number.isFinite(c.type) || c.type < 0 || c.type > 8)) {
      showToast(fixedUi("请选择合法的议价理由类型"), 'warning');
      return;
    }
    if (components.length > 20) {
      showToast(fixedUi("议价理由最多 20 条"), 'warning');
      return;
    }
    const links = externalLinks.map((u) => String(u || '').trim()).filter(Boolean);
    if (links.length > 5) {
      showToast(fixedUi("外部链接最多 5 个"), 'warning');
      return;
    }
    const priceItemList = [];
    for (const s of skuPrices) {
      const productSkuId = Number(s.productSkuId);
      const price = yuanToFenString(s.costYuan);
      if (!(productSkuId > 0) || !price) {
        showToast(fixedUi("请完善每个 SKU 的本次报价（元）"), 'warning');
        return;
      }
      priceItemList.push({
        productSkuId,
        price,
        orderId,
      });
    }
    if (!priceItemList.length) {
      showToast(fixedUi("缺少可报价的 SKU"), 'warning');
      return;
    }
    if (Number(row.isSizeSamePrice) === 1) {
      const first = priceItemList[0]?.price;
      if (priceItemList.some((s) => s.price !== first)) {
        showToast(fixedUi("该 SKC 要求所有尺码同价，请填写相同报价"), 'warning');
        return;
      }
    }
    const bargainReasonItem = {
      componentList: components,
    };
    if (links.length) {
      bargainReasonItem.externalLinkList = links;
    }
    onSubmit({
      createCostDiscusses: [{
        discussSn,
        orderId,
        bargainReasonList: [bargainReasonItem],
        priceItemList,
      }],
    });
  };

  const title = mode === 'requote'
    ? fixedUi('重新报价')
    : mode === 'accept'
      ? fixedUi("同意平台建议价")
      : fixedUi('拒绝议价');

  const productImg = normalizeDiscussImageUrl(row.mainPicUrl);
  const orderIdText = String(row.orderId || row.orderSn || row.discussSn || '').trim();
  const skuIdText = formatProductSkuIdList(row);

  return (
    <div className="tpo-batch-modal" role="presentation">
      <div className="tpo-batch-modal__backdrop" aria-hidden="true" />
      <div
        className="tpo-batch-modal__panel tpo-discuss-modal__panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="tpo-discuss-process-title"
      >
        <header className="tpo-batch-modal__head">
          <h2 id="tpo-discuss-process-title">{title}</h2>
          <button type="button" className="tpo-btn tpo-btn--secondary tpo-btn--sm" onClick={onClose}>{fixedUi("关闭")}</button>
        </header>
        <div className="tpo-batch-modal__body">
          {mode === 'requote' ? (
            <div className="tpo-discuss-requote">
              <section className="tpo-discuss-requote__row" aria-label={fixedUi("产品信息")}>
                <div className="tpo-discuss-requote__row-label">{fixedUi("产品信息")}</div>
                <div className="tpo-return-product tpo-discuss-requote__product-cell">
                  {productImg ? (
                    <TemuProductThumb
                      imageUrl={productImg}
                      className="tpo-return-product__img"
                      placeholderClassName="tpo-return-product__img tpo-return-product__img--empty"
                      alt={row.productTitle || fixedUi('议价商品')}
                    />
                  ) : (
                    <div className="tpo-return-product__img tpo-return-product__img--empty" />
                  )}
                  <div className="tpo-return-product__meta">
                    <HoverCopyField
                      value={row.productTitle || (orderIdText ? fixedUiTpl('核价单 {0}', orderIdText) : '')}
                      className="tpo-return-product__title"
                      popoverCopy
                    />
                    <HoverCopyField
                      label={fixedUi('核价单')}
                      value={orderIdText}
                      className="tpo-return-product__sku"
                    />
                    <HoverCopyField
                      label="SKU"
                      value={skuIdText === '—' ? '' : skuIdText}
                      className="tpo-return-product__sku"
                    />
                    <HoverCopyField
                      label={fixedUi("站点")}
                      value={formatSiteNameList(row) === '—' ? '' : formatSiteNameList(row)}
                      className="tpo-return-product__sku"
                    />
                    {row.spuName || row.skcName || row.supplierCode ? (
                      <>
                        <HoverCopyField label="SPU" value={row.spuName || row.spu_name || ''} className="tpo-return-product__sku" />
                        <HoverCopyField label="SKC" value={row.skcName || ''} className="tpo-return-product__sku" />
                        <HoverCopyField label={fixedUi("货号")} value={row.supplierCode || ''} className="tpo-return-product__sku" />
                      </>
                    ) : null}
                  </div>
                </div>
              </section>

              <section className="tpo-discuss-requote__row" aria-label={fixedUi("报价信息")}>
                <div className="tpo-discuss-requote__row-label">{fixedUi("报价信息")}</div>
                <div className="tpo-discuss-requote__prices">
                  {skuPrices.length ? skuPrices.map((s, idx) => (
                    <div key={s.productSkuId || s.skuCode || idx} className="tpo-discuss-requote__price-card">
                      <div className="tpo-discuss-requote__sku-tag">
                        SKU {s.productSkuId || s.skuCode || idx + 1}
                      </div>
                      <div className="tpo-discuss-requote__price-grid">
                        <div className="tpo-discuss-requote__price-item">
                          <span>{fixedUi("上次报价")}</span>
                          <strong>
                            {s.lastYuan !== '' && s.lastYuan != null
                              ? `${s.lastCurrency ? `${s.lastCurrency} ` : ''}${s.lastYuan}`
                              : '—'}
                          </strong>
                        </div>
                        <div className="tpo-discuss-requote__price-item">
                          <span>{fixedUi("平台建议价")}</span>
                          <strong>
                            {s.suggestYuan !== '' && s.suggestYuan != null
                              ? `${s.suggestCurrency ? `${s.suggestCurrency} ` : ''}${s.suggestYuan}`
                              : '—'}
                          </strong>
                        </div>
                        <label className="tpo-discuss-requote__price-item tpo-discuss-requote__price-item--edit">
                          <span>{fixedUi("本次报价（元）")}</span>
                          <input
                            className="tpo-input"
                            inputMode="decimal"
                            value={s.costYuan}
                            disabled={busy}
                            onChange={(e) => {
                              const v = e.target.value;
                              setSkuPrices((prev) => prev.map((item, i) => (i === idx ? { ...item, costYuan: v } : item)));
                            }}
                          />
                        </label>
                        <label className="tpo-discuss-requote__price-item tpo-discuss-requote__price-item--edit">
                          <span>{fixedUi("币种")}</span>
                          <input
                            className="tpo-input"
                            value={s.currency}
                            disabled={busy}
                            onChange={(e) => {
                              const v = e.target.value;
                              setSkuPrices((prev) => prev.map((item, i) => (i === idx ? { ...item, currency: v } : item)));
                            }}
                          />
                        </label>
                      </div>
                    </div>
                  )) : (
                    <p className="tpo-batch-modal__hint">{fixedUi("当前核价单未返回 SKU，无法填写报价")}</p>
                  )}
                </div>
              </section>

              <section className="tpo-discuss-requote__row" aria-label={fixedUi("议价理由")}>
                <div className="tpo-discuss-requote__row-label">{fixedUi("议价理由")}</div>
                <div className="tpo-discuss-requote__reasons">
                  <p className="tpo-discuss-requote__optional">
                    {fixedUi('必填，最多 20 条；请选择原因类型并填写说明')}
                  </p>
                  {reasonComponents.map((c, idx) => (
                    <div key={`reason-${idx}`} className="tpo-discuss-requote__reason-card">
                      <label className="tpo-discuss-requote__price-item tpo-discuss-requote__price-item--edit">
                        <span>{fixedUi("原因类型")}</span>
                        <select
                          className="tpo-input"
                          value={c.type}
                          disabled={busy}
                          onChange={(e) => {
                            const v = e.target.value;
                            setReasonComponents((prev) => prev.map((item, i) => (i === idx ? { ...item, type: v } : item)));
                          }}
                        >
                          {BARGAIN_REASON_TYPE_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>{fixedUi(opt.label)}</option>
                          ))}
                        </select>
                      </label>
                      <label className="tpo-discuss-field tpo-discuss-field--flush">
                        <span className="tpo-discuss-requote__optional">{fixedUi("具体原因（必填）")}</span>
                        <textarea
                          className="tpo-batch-modal__textarea"
                          value={c.reason}
                          maxLength={255}
                          rows={2}
                          disabled={busy}
                          onChange={(e) => {
                            const v = e.target.value;
                            setReasonComponents((prev) => prev.map((item, i) => (i === idx ? { ...item, reason: v } : item)));
                          }}
                          placeholder={fixedUi("说明重新报价原因")}
                        />
                      </label>
                      {reasonComponents.length > 1 ? (
                        <button
                          type="button"
                          className="tpo-btn tpo-btn--ghost tpo-btn--sm"
                          disabled={busy}
                          onClick={() => setReasonComponents((prev) => prev.filter((_, i) => i !== idx))}
                        >
                          {fixedUi('删除本条')}
                        </button>
                      ) : null}
                    </div>
                  ))}
                  {reasonComponents.length < 20 ? (
                    <button
                      type="button"
                      className="tpo-btn tpo-btn--secondary tpo-btn--sm"
                      disabled={busy}
                      onClick={() => setReasonComponents((prev) => [...prev, { type: '2', reason: '' }])}
                    >
                      <Plus size={14} aria-hidden="true" /> {fixedUi('添加理由')}
                    </button>
                  ) : null}
                </div>
              </section>

              <section className="tpo-discuss-requote__row" aria-label={fixedUi("外部链接")}>
                <div className="tpo-discuss-requote__row-label">{fixedUi("外部链接")}</div>
                <div className="tpo-discuss-requote__links">
                  <p className="tpo-discuss-requote__optional">
                    {fixedUi('对应 externalLinkList，选填，最多 5 个（竞品/材料说明等 URL）')}
                  </p>
                  {externalLinks.map((link, idx) => (
                    <div key={`link-${idx}`} className="tpo-discuss-requote__link-row">
                      <input
                        className="tpo-input"
                        value={link}
                        disabled={busy}
                        placeholder="https://"
                        onChange={(e) => {
                          const v = e.target.value;
                          setExternalLinks((prev) => prev.map((item, i) => (i === idx ? v : item)));
                        }}
                      />
                      {externalLinks.length > 1 ? (
                        <button
                          type="button"
                          className="tpo-btn tpo-btn--ghost tpo-btn--sm"
                          disabled={busy}
                          onClick={() => setExternalLinks((prev) => prev.filter((_, i) => i !== idx))}
                        >
                          {fixedUi("移除")}
                        </button>
                      ) : null}
                    </div>
                  ))}
                  {externalLinks.length < 5 ? (
                    <button
                      type="button"
                      className="tpo-btn tpo-btn--secondary tpo-btn--sm"
                      disabled={busy}
                      onClick={() => setExternalLinks((prev) => [...prev, ''])}
                    >
                      <Plus size={14} aria-hidden="true" /> {fixedUi('添加链接')}
                    </button>
                  ) : null}
                </div>
              </section>
            </div>
          ) : (
            <p className="tpo-batch-modal__hint">
              {mode === 'accept'
                ? fixedUi('同意后将按平台建议价确认核价单。')
                : fixedUi('拒绝后核价单将按平台规则结束或进入重新报价。')}
            </p>
          )}
        </div>
        <footer className="tpo-batch-modal__foot">
          <button type="button" className="tpo-btn tpo-btn--secondary" disabled={busy} onClick={onClose}>{fixedUi("取消")}</button>
          <button type="button" className="tpo-btn tpo-btn--primary" disabled={busy} onClick={submit}>
            {busy ? fixedUi("提交中…") : fixedUi('确认提交')}
          </button>
        </footer>
      </div>
    </div>
  );
}

export default function DiscussListPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    activeShop,
    loading: shopLoading,
    shopDimensionTabs,
    concreteScopeId,
    scopedShops = [],
  } = useTemuPageShopScope();
  const shopId = concreteScopeId;
  const pageShop = concreteScopeId
    ? (scopedShops.find((x) => String(x.id) === String(concreteScopeId)) || activeShop)
    : activeShop;
  const caps = temuShopCapabilities(pageShop || activeShop);
  const canDiscuss = Boolean(caps.canDiscussCost);
  const { startJob, onTrackDone, isBusy } = useTemuSyncJobs();

  const initialStatus = searchParams.get('status') || '';
  const [status, setStatus] = useState(initialStatus);
  const [filterDraft, setFilterDraft] = useState({
    discussSn: '',
    skcName: '',
    supplierCode: '',
    discussType: '',
  });
  const [query, setQuery] = useState({
    discussSn: '',
    skcName: '',
    supplierCode: '',
    discussType: '',
    _nonce: 0,
  });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(TEMU_OPS_DEFAULT_PAGE_SIZE);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [statusCounts, setStatusCounts] = useState(() => ({}));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [processRow, setProcessRow] = useState(null);
  const [processMode, setProcessMode] = useState('requote');
  const [processing, setProcessing] = useState(false);
  const [batchBusy, setBatchBusy] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState(() => new Set());
  const selectedRowsRef = useRef(new Map());
  const [batchModalField, setBatchModalField] = useState(null);
  const [lastSyncedAt, setLastSyncedAt] = useState('');
  const [persistedSyncedAt, setPersistedSyncedAt] = useState('');
  const [syncModalOpen, setSyncModalOpen] = useState(false);
  const [autoRuleModalOpen, setAutoRuleModalOpen] = useState(false);

  useEffect(() => {
    const next = searchParams.get('status') || '';
    setStatus(next);
    setPage(1);
  }, [searchParams]);

  useEffect(() => {
    setPage(1);
    setRows([]);
    setTotal(0);
    setError('');
    setSelectedKeys(new Set());
    selectedRowsRef.current.clear();
    setLastSyncedAt('');
    setPersistedSyncedAt('');
  }, [shopId]);

  useEffect(() => {
    setSelectedKeys(new Set());
    selectedRowsRef.current.clear();
  }, [query._nonce, status]);

  useEffect(() => {
    if (!shopId || !canDiscuss) {
      setPersistedSyncedAt('');
      return undefined;
    }
    let cancelled = false;
    temuPlatformAPI.getShopSyncStatus(shopId)
      .then((res) => {
        if (cancelled) return;
        const modules = Array.isArray(res?.modules) ? res.modules : [];
        const mod = modules.find((m) => String(m.module || '').toUpperCase() === 'DISCUSS');
        const at = mod?.lastSyncEndAt || mod?.lastRunAt || '';
        setPersistedSyncedAt(at ? String(at) : '');
      })
      .catch(() => {
        if (!cancelled) setPersistedSyncedAt('');
      });
    return () => { cancelled = true; };
  }, [shopId, canDiscuss]);

  const load = useCallback(async () => {
    if (!shopId || !canDiscuss) {
      setRows([]);
      setTotal(0);
      setStatusCounts({});
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const body = {
        source: 'cached',
        pageNum: page,
        pageSize,
        discussSnList: parseBatchTokens(query.discussSn),
        skcNameList: parseBatchTokens(query.skcName),
        supplierCodeList: parseBatchTokens(query.supplierCode),
      };
      if (status !== '' && status != null) {
        body.discussStatus = Number(status);
      }
      if (query.discussType !== '' && query.discussType != null) {
        body.discussType = Number(query.discussType);
      }
      const res = await temuPlatformAPI.listDiscussOrders(shopId, body);
      const list = extractDiscussList(res);
      const totalCount = extractDiscussTotal(res, list.length);
      setRows(list);
      setTotal(totalCount);
      const counts = res?.statusCounts && typeof res.statusCounts === 'object' ? res.statusCounts : {};
      setStatusCounts(counts);
      if (String(status) === '1') {
        rememberDiscussPendingForHome(shopId, totalCount);
      } else {
        const pending = Number(counts?.['1'] ?? counts?.[1]);
        if (Number.isFinite(pending) && pending >= 0) {
          rememberDiscussPendingForHome(shopId, pending);
        }
      }
    } catch (e) {
      setRows([]);
      setTotal(0);
      setStatusCounts({});
      setError(e?.message || fixedUi('加载核价单失败'));
    } finally {
      setLoading(false);
    }
  }, [shopId, canDiscuss, page, pageSize, status, query]);

  useEffect(() => {
    if (shopLoading) return;
    load();
  }, [load, shopLoading]);

  const applyDiscussAutoRulesAfterSync = useCallback(async () => {
    if (!shopId || !canDiscuss) return;
    let rules = [];
    try {
      const prefRes = await temuPlatformAPI.getPreferences();
      const prefs = prefRes?.preferences || prefRes || {};
      rules = readDiscussAutoRulesFromPreferences(prefs, shopId)
        .filter((r) => r && r.enabled !== false);
    } catch {
      return;
    }
    if (!rules.length) return;

    let pendingRows = [];
    try {
      const res = await temuPlatformAPI.listDiscussOrders(shopId, {
        source: 'cached',
        pageNum: 1,
        pageSize: 100,
        discussStatus: 1,
      });
      pendingRows = extractDiscussList(res).filter((row) => {
        const sn = row?.discussSn || row?.orderSn || row?.orderId;
        return sn && Number(row.discussStatus ?? row.orderStatus ?? row.status) === 1;
      });
    } catch {
      return;
    }
    if (!pendingRows.length) return;

    const acceptInfos = [];
    const rejectInfos = [];
    for (const row of pendingRows) {
      const action = evaluateDiscussAutoAction(row, rules);
      if (action === 'accept') acceptInfos.push(toDiscussConfirmInfo(row, 1));
      else if (action === 'reject') rejectInfos.push(toDiscussConfirmInfo(row, 2));
    }
    if (!acceptInfos.length && !rejectInfos.length) return;

    let ok = 0;
    let fail = 0;
    try {
      if (acceptInfos.length) {
        const res = await temuPlatformAPI.processDiscussOrder(shopId, { confirmInfos: acceptInfos });
        ok += Number(res?.successCount) || 0;
        fail += Number(res?.failCount) || 0;
      }
      if (rejectInfos.length) {
        const res = await temuPlatformAPI.processDiscussOrder(shopId, { confirmInfos: rejectInfos });
        ok += Number(res?.successCount) || 0;
        fail += Number(res?.failCount) || 0;
      }
      const parts = [];
      if (acceptInfos.length) parts.push(fixedUiTpl('同意 {0}', acceptInfos.length));
      if (rejectInfos.length) parts.push(fixedUiTpl('拒绝 {0}', rejectInfos.length));
      showToast(
        fixedUiTpl('自动规则已执行：{0}{1}', parts.join(fixedUi('，')), fail ? fixedUiTpl('，失败 {0}', fail) : ok ? fixedUiTpl('，成功 {0}', ok) : ''),
        fail && !ok ? 'error' : 'success',
      );
    } catch (e) {
      showToast(e?.message || fixedUi('自动规则执行失败'), 'error');
    }
  }, [shopId, canDiscuss]);

  const handleSync = useCallback(async (options = {}) => {
    if (!shopId || !canDiscuss) {
      showToast(fixedUi("请先选择可用店铺"), 'warning');
      return;
    }
    try {
      const track = await startJob({
        kind: 'discuss',
        shopId,
        get label() { return fixedUi("同步核价单"); },
        payload: {
          maxPages: options?.maxPages,
        },
      });
      if (!track?.id) return;
      const result = await new Promise((resolve) => {
        const unsub = onTrackDone(track.id, (r) => {
          unsub();
          resolve(r);
        });
      });
      if (!result?.ok) return;
      const finishedAt = new Date().toISOString();
      setLastSyncedAt(finishedAt);
      setPersistedSyncedAt(finishedAt);
      await applyDiscussAutoRulesAfterSync();
      setQuery((prev) => ({ ...prev, _nonce: prev._nonce + 1 }));
      setPage(1);
    } catch (e) {
      if (e?.code === 'SYNC_IN_PROGRESS' || e?.code === 'ABORT') {
        return;
      }
      if (e?.message) showToast(e.message, 'error');
      throw e;
    }
  }, [shopId, canDiscuss, startJob, onTrackDone, applyDiscussAutoRulesAfterSync]);

  const { refresh: guardedSync, refreshing: syncingLocal, cooldownSeconds: syncCooldown } = useGuardedRefresh(
    handleSync,
    { minIntervalMs: 3000 },
  );
  const syncing = syncingLocal || isBusy('discuss', shopId);
  const latestSyncedAt = lastSyncedAt || persistedSyncedAt;
  const lastSyncedLabel = syncing
    ? fixedUi("同步中…")
    : (latestSyncedAt
      ? `${fixedUi('最近同步')}：${formatSiteListSyncedAt(latestSyncedAt)}`
      : fixedUi('尚未同步，请点击「拉取 TEMU 后台数据」'));

  const statusTabs = useMemo(() => (
    <div className="tpo-status-tabs" role="tablist" aria-label={fixedUi("核价单状态")}>
      {STATUS_TABS.map((tab) => {
        const countKey = tab.value === '' ? 'all' : String(tab.value);
        const count = Number(statusCounts?.[countKey] ?? 0);
        return (
          <button
            key={tab.value || 'all'}
            type="button"
            role="tab"
            aria-selected={String(status) === String(tab.value)}
            className={`tpo-status-tab${String(status) === String(tab.value) ? ' tpo-status-tab--active' : ''}`}
            onClick={() => {
              const next = new URLSearchParams(searchParams);
              if (tab.value === '') next.delete('status');
              else next.set('status', tab.value);
              setSearchParams(next, { replace: true });
              setPage(1);
            }}
          >
            {fixedUi(tab.label)}
            <span className="tpo-status-tab__count">{Number.isFinite(count) ? count : 0}</span>
          </button>
        );
      })}
    </div>
  ), [status, searchParams, setSearchParams, statusCounts]);

  const listTabs = useMemo(
    () => stackShopDimensionTabs(shopDimensionTabs, statusTabs),
    [shopDimensionTabs, statusTabs],
  );

  const rowKey = (row) => String(row.discussSn || `${row.skcName}-${row.serialNumber}`);
  const selectableKeys = useMemo(
    () => rows.map(rowKey).filter(Boolean),
    [rows],
  );
  const allSelected = selectableKeys.length > 0 && selectableKeys.every((k) => selectedKeys.has(k));

  const toggleAll = () => {
    setSelectedKeys((prev) => togglePageSelection(prev, selectableKeys, allSelected));
  };

  const toggleOne = (key, row) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
        selectedRowsRef.current.delete(key);
      } else {
        next.add(key);
        if (row) selectedRowsRef.current.set(key, row);
      }
      return next;
    });
    if (row && !selectedRowsRef.current.has(key)) {
      selectedRowsRef.current.set(key, row);
    }
  };

  useEffect(() => {
    rows.forEach((row) => {
      const key = rowKey(row);
      if (selectedKeys.has(key)) selectedRowsRef.current.set(key, row);
    });
  }, [rows, selectedKeys]);

  const onQuery = () => {
    setQuery({
      discussSn: filterDraft.discussSn,
      skcName: filterDraft.skcName,
      supplierCode: filterDraft.supplierCode,
      discussType: filterDraft.discussType,
      _nonce: query._nonce + 1,
    });
    setPage(1);
  };

  const onReset = () => {
    setFilterDraft({ discussSn: '', skcName: '', supplierCode: '', discussType: '' });
    setQuery({ discussSn: '', skcName: '', supplierCode: '', discussType: '', _nonce: query._nonce + 1 });
    setPage(1);
  };

  const batchModalMeta = {
    discussSn: { title: fixedUi('批量查询核价单号'), placeholder: fixedUi('每行一个核价单号，或用逗号分隔') },
    skcName: { title: fixedUi('批量查询 SKC'), placeholder: fixedUi('每行一个平台 SKC，或用逗号分隔') },
    supplierCode: { title: fixedUi('批量查询货号'), placeholder: fixedUi('每行一个商家货号，或用逗号分隔') },
  }[batchModalField] || { get title() { return fixedUi("批量查询"); }, placeholder: fixedUi('每行一条，或用逗号分隔') };

  const openProcessModal = (row, mode) => {
    setProcessMode(mode);
    setProcessRow({ ...row, __shopId: shopId });
  };

  const submitConfirmAction = async (row, auditType) => {
    if (!shopId || !row?.discussSn) return;
    const isAccept = String(auditType) === '1';
    const ok = await showTemuConfirm({
      title: isAccept ? fixedUi("同意平台建议价") : fixedUi('拒绝议价'),
      message: isAccept
        ? fixedUiTpl('确认同意核价单「{0}」的平台建议价？', row.discussSn)
        : fixedUiTpl('确认拒绝核价单「{0}」？', row.discussSn),
      tone: isAccept ? 'info' : 'danger',
      confirmText: isAccept ? fixedUi('确认同意') : fixedUi('确认拒绝'),
      cancelText: fixedUi('取消'),
    });
    if (!ok) return;
    setProcessing(true);
    try {
      const res = await temuPlatformAPI.processDiscussOrder(shopId, {
        confirmInfos: [{
          discussAuditType: String(auditType),
          discussSn: row.discussSn,
          orderId: Number(row.orderId || row.orderSn || row.discussSn) || undefined,
        }],
      });
      const success = Number(res?.successCount) || 0;
      const fail = Number(res?.failCount) || 0;
      if (fail > 0 && success === 0) {
        showToast(res?.msg || fixedUi('处理失败'), 'error');
      } else {
        showToast(success ? fixedUiTpl('处理成功 {0} 单', success) : (res?.msg || fixedUi('已提交')), 'success');
        await load();
      }
    } catch (e) {
      showToast(e?.message || fixedUi('处理核价单失败'), 'error');
    } finally {
      setProcessing(false);
    }
  };

  const handleProcess = async (payload) => {
    if (!shopId || !processRow) return;
    setProcessing(true);
    try {
      const res = await temuPlatformAPI.processDiscussOrder(shopId, payload);
      const ok = Number(res?.successCount) || 0;
      const fail = Number(res?.failCount) || 0;
      if (fail > 0 && ok === 0) {
        showToast(res?.msg || fixedUi('处理失败'), 'error');
      } else {
        showToast(ok ? fixedUiTpl('处理成功 {0} 单', ok) : (res?.msg || fixedUi('已提交')), 'success');
        setProcessRow(null);
        await load();
      }
    } catch (e) {
      showToast(e?.message || fixedUi('处理核价单失败'), 'error');
    } finally {
      setProcessing(false);
    }
  };

  const batchConfirm = async (auditType) => {
    if (!shopId || selectedKeys.size === 0) return;
    const pending = [];
    selectedKeys.forEach((key) => {
      const row = selectedRowsRef.current.get(key) || rows.find((r) => rowKey(r) === key);
      if (row && Number(row.discussStatus) === 1 && row.discussSn) {
        pending.push({
          discussAuditType: String(auditType),
          discussSn: row.discussSn,
          orderId: Number(row.orderId || row.orderSn || row.discussSn) || undefined,
        });
      }
    });
    if (!pending.length) {
      showToast(fixedUi("请选择待供应商确认的核价单"), 'warning');
      return;
    }
    setBatchBusy(true);
    try {
      const res = await temuPlatformAPI.processDiscussOrder(shopId, { confirmInfos: pending });
      const ok = Number(res?.successCount) || 0;
      const fail = Number(res?.failCount) || 0;
      if (fail > 0 && ok === 0) {
        showToast(res?.msg || fixedUi('批量处理失败'), 'error');
      } else {
        showToast(fixedUiTpl('已处理 {0} 单{1}', ok || pending.length, fail ? fixedUiTpl('，失败 {0}', fail) : ''), 'success');
        setSelectedKeys(new Set());
        selectedRowsRef.current.clear();
        await load();
      }
    } catch (e) {
      showToast(e?.message || fixedUi('批量处理失败'), 'error');
    } finally {
      setBatchBusy(false);
    }
  };

  const exportRows = (source) => {
    if (!source.length) {
      showToast(fixedUi("暂无可导出数据"), 'warning');
      return;
    }
    downloadCsv(
      `temu-discuss-${Date.now()}.csv`,
      ['orderId', 'orderStatus', 'siteNameList', 'supplyPrice', 'priceCurrency', 'suggestSupplyPrice', 'suggestPriceCurrency', 'canBargain', 'productSkuIdList'],
      source.map((row) => ({
        orderId: row.orderId || row.orderSn || row.discussSn || '',
        orderStatus: discussStatusLabel(row.discussStatus ?? row.orderStatus ?? row.status),
        siteNameList: formatSiteNameList(row),
        supplyPrice: row.supplyPrice ?? row.declarePrice ?? '',
        priceCurrency: row.priceCurrency || '',
        suggestSupplyPrice: row.suggestSupplyPrice ?? row.suggestPrice ?? '',
        suggestPriceCurrency: row.suggestPriceCurrency || '',
        canBargain: formatCanBargain(row.canBargain ?? row.negotiable),
        productSkuIdList: formatProductSkuIdList(row),
      })),
    );
    showToast(fixedUiTpl('已导出 {0} 条', source.length), 'success');
  };

  const exportFiltered = async () => {
    if (!shopId || !canDiscuss) {
      showToast(fixedUi("请先选择可用店铺"), 'warning');
      return;
    }
    if (exporting) return;
    setExporting(true);
    try {
      const all = await fetchAllPages(async (pageNo, size) => {
        const body = {
          source: 'cached',
          pageNum: pageNo,
          pageSize: size,
          discussSnList: parseBatchTokens(query.discussSn),
          skcNameList: parseBatchTokens(query.skcName),
          supplierCodeList: parseBatchTokens(query.supplierCode),
        };
        if (status !== '' && status != null) {
          body.discussStatus = Number(status);
        }
        if (query.discussType !== '' && query.discussType != null) {
          body.discussType = Number(query.discussType);
        }
        const res = await temuPlatformAPI.listDiscussOrders(shopId, body);
        const list = extractDiscussList(res);
        return { items: list, total: extractDiscussTotal(res, list.length) };
      });
      exportRows(all);
    } catch (e) {
      showToast(e?.message || fixedUi("导出筛选失败"), 'error');
    } finally {
      setExporting(false);
    }
  };

  if (!shopLoading && shopId && !canDiscuss) {
    return (
      <ProductListCard>
        <p className="tpo-empty-hint">
          {fixedUi('当前为自运营店铺，核价单不适用。请从侧栏进入「价格处理 → 调价单」。')}
        </p>
      </ProductListCard>
    );
  }

  const colSpan = 9;

  return (
    <>
      <TemuSyncProgressSlot kinds={['discuss']} />
      <ProductFilterCard
        toolbar={(
          <TemuOpsListToolbar
            selectedCount={selectedKeys.size}
            busy={loading || processing || batchBusy || syncing || exporting}
            syncKinds={['discuss']}
            onSync={() => {
              if (!shopId || !canDiscuss) {
                showToast(fixedUi("请先选择可用店铺"), 'warning');
                return;
              }
              setSyncModalOpen(true);
            }}
            syncing={syncing}
            syncCooldownSeconds={syncCooldown}
            syncDisabled={!shopId || syncing || !canDiscuss}
            lastSyncedLabel={lastSyncedLabel}
            syncHint={fixedUi('从 TEMU 后台拉取核价单，更新到 IPE 本地。')}
            exportLabel={opsExportLabel(exporting)}
            exportDisabled={!shopId || exporting}
            exportMenu={buildOpsExportMenu({
              exporting,
              filteredCount: total,
              pageCount: rows.length,
              selectedCount: selectedKeys.size,
              onExportFiltered: exportFiltered,
              onExportPage: () => exportRows(rows),
              onExportSelected: () => exportRows(
                [...selectedKeys].map((k) => selectedRowsRef.current.get(k)).filter(Boolean),
              ),
            })}
            extraActions={[
              {
                key: 'auto-rules',
                get label() { return fixedUi("自动规则"); },
                hint: fixedUi(DISCUSS_AUTO_RULE_HINT),
                disabled: !shopId || !canDiscuss || loading || processing || batchBusy || syncing,
                onClick: () => setAutoRuleModalOpen(true),
              },
              {
                key: 'batch-accept',
                label: batchBusy ? fixedUi('处理中…') : fixedUi('批量同意'),
                tone: 'primary',
                disabled: selectedKeys.size === 0 || batchBusy,
                onClick: () => batchConfirm(1),
              },
              {
                key: 'batch-reject',
                label: fixedUi('批量拒绝'),
                tone: 'danger',
                disabled: selectedKeys.size === 0 || batchBusy,
                onClick: () => batchConfirm(2),
              },
            ]}
          />
        )}
      >
        <div className="tpo-filter-bar">
          <div className="tpo-filter-bar__fields">
            <div className="tpo-field tpo-field--inline tpo-field--batch tpo-field--w300">
              <span className="tpo-field__label">{fixedUi("核价单号")}</span>
              <input
                className="tpo-input"
                value={filterDraft.discussSn}
                disabled={!shopId || !canDiscuss}
                placeholder={fixedUi("支持批量，逗号分隔")}
                onChange={(e) => setFilterDraft((prev) => ({ ...prev, discussSn: e.target.value }))}
                onKeyDown={(e) => e.key === 'Enter' && onQuery()}
              />
              <button
                type="button"
                className="tpo-batch-add-btn"
                aria-label={fixedUi("批量输入核价单号")}
                title={fixedUi("批量查询")}
                disabled={!shopId || !canDiscuss}
                onClick={() => setBatchModalField('discussSn')}
              >
                <Plus size={16} strokeWidth={2.25} aria-hidden="true" />
              </button>
            </div>
            <div className="tpo-field tpo-field--inline tpo-field--batch tpo-field--w300">
              <span className="tpo-field__label">SKC</span>
              <input
                className="tpo-input"
                value={filterDraft.skcName}
                disabled={!shopId || !canDiscuss}
                placeholder={fixedUi("平台 SKC，支持批量")}
                onChange={(e) => setFilterDraft((prev) => ({ ...prev, skcName: e.target.value }))}
                onKeyDown={(e) => e.key === 'Enter' && onQuery()}
              />
              <button
                type="button"
                className="tpo-batch-add-btn"
                aria-label={fixedUi("批量输入 SKC")}
                title={fixedUi("批量查询")}
                disabled={!shopId || !canDiscuss}
                onClick={() => setBatchModalField('skcName')}
              >
                <Plus size={16} strokeWidth={2.25} aria-hidden="true" />
              </button>
            </div>
            <div className="tpo-field tpo-field--inline tpo-field--batch tpo-field--w300">
              <span className="tpo-field__label">{fixedUi("货号")}</span>
              <input
                className="tpo-input"
                value={filterDraft.supplierCode}
                disabled={!shopId || !canDiscuss}
                placeholder={fixedUi("商家货号，支持批量")}
                onChange={(e) => setFilterDraft((prev) => ({ ...prev, supplierCode: e.target.value }))}
                onKeyDown={(e) => e.key === 'Enter' && onQuery()}
              />
              <button
                type="button"
                className="tpo-batch-add-btn"
                aria-label={fixedUi("批量输入货号")}
                title={fixedUi("批量查询")}
                disabled={!shopId || !canDiscuss}
                onClick={() => setBatchModalField('supplierCode')}
              >
                <Plus size={16} strokeWidth={2.25} aria-hidden="true" />
              </button>
            </div>
          </div>
          <div className="tpo-filter-bar__actions">
            <button
              type="button"
              className="tpo-btn tpo-btn--secondary tpo-btn--sm"
              disabled={!shopId || !canDiscuss}
              onClick={onReset}
            >
              {fixedUi("重置")}
            </button>
            <button
              type="button"
              className="tpo-btn tpo-btn--primary tpo-btn--sm"
              disabled={!shopId || loading || !canDiscuss}
              onClick={onQuery}
            >
              {fixedUi("查询")}
            </button>
          </div>
        </div>
        {error ? <div className="tpo-ops-error" role="alert">{error}</div> : null}
      </ProductFilterCard>

      <BatchQueryModal
        open={Boolean(batchModalField)}
        title={batchModalMeta.title}
        initialValue={batchModalField ? filterDraft[batchModalField] : ''}
        placeholder={batchModalMeta.placeholder}
        onConfirm={(tokens) => {
          if (!batchModalField) return;
          setFilterDraft((prev) => ({ ...prev, [batchModalField]: joinBatchTokens(tokens) }));
        }}
        onClose={() => setBatchModalField(null)}
      />

      <ProductListCard
        tabs={listTabs}
        loading={Boolean(shopId) && loading}
        loadingLabel={fixedUi('正在查询核价单…')}
      >
        <div className="tpo-drafts__table-wrap tpo-ops-table-wrap">
          <table className="tpo-publish__table tpo-drafts__table tpo-ops-table tpo-ops-table--price">
            <colgroup>
              <col className="tpo-ops-col tpo-ops-col--check temu-product-col--check" />
              <col className="tpo-ops-col tpo-ops-col--absorb" />
              <col className="tpo-ops-col tpo-ops-col--share" />
              <col className="tpo-ops-col tpo-ops-col--share" />
              <col className="tpo-ops-col tpo-ops-col--share" />
              <col className="tpo-ops-col tpo-ops-col--sku-price" />
              <col className="tpo-ops-col tpo-ops-col--sku-price" />
              <col className="tpo-ops-col tpo-ops-col--sku-price" />
              <col className="tpo-ops-col tpo-ops-col--ops tpo-ops-col--share" />
            </colgroup>
            <thead>
              <tr>
                <th
                  scope="col"
                  className="tpo-ops-th tpo-ops-th--check temu-product-cell--spu-check"
                  onClick={() => {
                    if (!shopId || loading || selectableKeys.length === 0) return;
                    toggleAll();
                  }}
                >
                  <input
                    type="checkbox"
                    aria-label={fixedUi("全选核价单")}
                    disabled={!shopId || loading || selectableKeys.length === 0}
                    checked={allSelected}
                    onClick={(e) => e.stopPropagation()}
                    onChange={toggleAll}
                  />
                </th>
                <th scope="col">{fixedUi("产品信息")}</th>
                <th scope="col">{fixedUi("核价单号")}</th>
                <th scope="col">{fixedUi("状态")}</th>
                <th scope="col">{fixedUi("站点")}</th>
                <th scope="col" className="tpo-sku-price-group tpo-sku-price-group--start">SKU</th>
                <th scope="col" className="tpo-sku-price-group tpo-sku-price-group--mid">{fixedUi("申报价")}</th>
                <th scope="col" className="tpo-sku-price-group tpo-sku-price-group--end">{fixedUi("建议价")}</th>
                <th scope="col" className="tpo-ops-th--ops">{fixedUi("操作")}</th>
              </tr>
            </thead>
            <tbody>
              {!shopId || loading || rows.length === 0 ? (
                <tr>
                  <td colSpan={colSpan} className="tpo-certs-empty-cell">
                    <div className="tpo-certs-empty">
                      <p className="tpo-certs-empty__title">
                        {!shopId ? fixedUi("请先选择店铺") : loading ? fixedUi("加载中…") : '暂无核价单'}
                      </p>
                      {!shopId || loading ? null : (
                        <p className="tpo-certs-empty__desc">{fixedUi("可点「拉取 TEMU 后台数据」获取平台核价单")}</p>
                      )}
                    </div>
                  </td>
                </tr>
              ) : (
                rows.map((row) => {
                  const key = rowKey(row);
                  const img = normalizeDiscussImageUrl(row.mainPicUrl || row.imageUrl);
                  const statusCode = row.discussStatus ?? row.orderStatus ?? row.status;
                  const canProcess = Number(statusCode) === 1;
                  const canRequote = row.canBargain === true || row.negotiable === true;
                  const orderId = row.orderId || row.orderSn || row.discussSn || '';
                  const title = fixedUi(row.productTitle || row.productName || '核价单');
                  const colorValue = pickPricingColorLabel(row);
                  const skcValue = String(row.skcName || row.skc_name || row.skcId || '').trim();
                  const supplierCode = String(row.supplierCode || row.extCode || row.productSn || '').trim();
                  const sites = extractSiteNameList(row);
                  const priceLines = formatReviewSkuPriceLines(row);
                  return (
                    <tr key={key}>
                      <td
                        className="tpo-ops-td tpo-ops-td--check tpo-ops-td--vmid temu-product-cell--spu-check"
                        onClick={() => toggleOne(key, row)}
                      >
                        <input
                          type="checkbox"
                          aria-label={fixedUiTpl('选择 {0}', orderId || key)}
                          checked={selectedKeys.has(key)}
                          onClick={(e) => e.stopPropagation()}
                          onChange={() => toggleOne(key, row)}
                        />
                      </td>
                      <td className="tpo-ops-td tpo-ops-td--product">
                        <div className="tpo-return-product">
                          {img ? (
                            <TemuProductThumb
                              imageUrl={img}
                              className="tpo-return-product__img"
                              placeholderClassName="tpo-return-product__img tpo-return-product__img--empty"
                              alt={title}
                            />
                          ) : (
                            <div className="tpo-return-product__img tpo-return-product__img--empty" />
                          )}
                          <div className="tpo-return-product__meta">
                            <HoverCopyField
                              value={title}
                              className="tpo-return-product__title"
                              popoverCopy
                            />
                            <HoverCopyField
                              label={fixedUi("颜色")}
                              value={colorValue}
                              className="tpo-return-product__sku"
                            />
                            <HoverCopyField
                              label="SKC"
                              value={skcValue}
                              className="tpo-return-product__sku"
                            />
                            <HoverCopyField
                              label={fixedUi("货号")}
                              value={supplierCode}
                              className="tpo-return-product__sku"
                            />
                          </div>
                        </div>
                      </td>
                      <td className="tpo-ops-td--vmid">
                        <HoverCopyField
                          value={orderId}
                          display={orderId ? <strong>{orderId}</strong> : '—'}
                        />
                      </td>
                      <td className="tpo-ops-td--vmid">
                        <span className={`tpo-records__status tpo-records__status--${discussStatusModifier(statusCode)}`}>
                          {discussStatusLabel(statusCode)}
                        </span>
                      </td>
                      <td className="tpo-rrp-site-cell tpo-ops-td--vmid">
                        {sites.length ? (
                          <div className="tpo-site-lines">
                            {sites.map((site) => (
                              <div key={site} className="tpo-site-lines__item">{localizedSiteLabel(site)}</div>
                            ))}
                          </div>
                        ) : '—'}
                      </td>
                      <td className="tpo-sku-price-group tpo-sku-price-group--start">
                        <div className="tpo-rrp-price-lines">
                          {(priceLines.lines?.length ? priceLines.lines : [{ key: 'empty', head: '' }]).map((line) => (
                            <div key={`sku-${line.key}`} className="tpo-rrp-price-lines__item">
                              <div className="tpo-rrp-price-lines__sku">
                                {line.head || '—'}
                              </div>
                            </div>
                          ))}
                        </div>
                      </td>
                      <td
                        className="tpo-sku-price-group tpo-sku-price-group--mid"
                        title={priceLines.lines.map((l) => l.supply).join('\n')}
                      >
                        <div className="tpo-rrp-price-lines">
                          {(priceLines.lines?.length ? priceLines.lines : [{ key: 'empty', supply: '—' }]).map((line) => (
                            <div key={`supply-${line.key}`} className="tpo-rrp-price-lines__item">
                              <div className="tpo-rrp-price-lines__amt">{line.supply || '—'}</div>
                            </div>
                          ))}
                        </div>
                      </td>
                      <td
                        className="tpo-sku-price-group tpo-sku-price-group--end"
                        title={priceLines.lines.map((l) => l.suggest).join('\n')}
                      >
                        <div className="tpo-rrp-price-lines">
                          {(priceLines.lines?.length ? priceLines.lines : [{ key: 'empty', suggest: '—' }]).map((line) => (
                            <div key={`suggest-${line.key}`} className="tpo-rrp-price-lines__item">
                              <div className="tpo-rrp-price-lines__amt">{line.suggest || '—'}</div>
                            </div>
                          ))}
                        </div>
                      </td>
                      <td className="tpo-certs-td tpo-certs-td--ops tpo-ops-td--ops tpo-ops-td--vmid">
                        <div className="tpo-drafts__actions tpo-discuss-ops">
                          {canProcess ? (
                            <>
                              <button
                                type="button"
                                className="tpo-btn tpo-btn--primary tpo-btn--sm"
                                disabled={processing || batchBusy}
                                onClick={() => submitConfirmAction(row, 1)}
                              >
                                同意
                              </button>
                              <button
                                type="button"
                                className="tpo-btn tpo-btn--danger tpo-btn--sm"
                                disabled={processing || batchBusy}
                                onClick={() => submitConfirmAction(row, 2)}
                              >
                                拒绝
                              </button>
                              {canRequote ? (
                                <button
                                  type="button"
                                  className="tpo-btn tpo-btn--secondary tpo-btn--sm"
                                  disabled={processing || batchBusy}
                                  onClick={() => openProcessModal(row, 'requote')}
                                >
                                  重新报价
                                </button>
                              ) : null}
                            </>
                          ) : (
                            <button type="button" className="tpo-btn tpo-btn--ghost tpo-btn--sm" disabled>
                              不可处理
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        <TemuOpsPager
          page={page}
          pageSize={pageSize}
          total={total}
          onPageChange={setPage}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(1);
          }}
        />
      </ProductListCard>

      <ProcessDiscussModal
        open={Boolean(processRow)}
        row={processRow}
        busy={processing}
        initialMode={processMode}
        onClose={() => (!processing ? setProcessRow(null) : null)}
        onSubmit={handleProcess}
      />
      <TemuSyncMaxPagesModal
        open={syncModalOpen}
        title={fixedUi("同步核价单")}
        hint=""
        pageSizeHint={200}
        defaultMaxPages={10}
        absMaxPages={100}
        confirming={false}
        onClose={() => {
          if (!syncing) setSyncModalOpen(false);
        }}
        onConfirm={async ({ maxPages }) => {
          setSyncModalOpen(false);
          try {
            await guardedSync({ maxPages });
          } catch (_) {
            // toast 已在 handleSync / SyncJobContext 处理
          }
        }}
      />
      <DiscussAutoRuleModal
        open={autoRuleModalOpen}
        shopId={shopId}
        busy={processing || batchBusy || syncing}
        onClose={() => setAutoRuleModalOpen(false)}
      />
    </>
  );
}
